import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from "react-router-dom";

import BaseWrapper from "../../components/shared/BaseWrapper";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import {
  Box,
  Button,
  Typography,
  Autocomplete,
  TextField,
  CircularProgress,
} from '@mui/material';
import { getCacheClients } from '../API/clientAPI';
import ContractsTable from './components/ContractsTable';
import InvoicesTable from './components/InvoicesTable';

function ManageContracts() {
  const navigate = useNavigate();
  const location = useLocation();

  const initialSelectedClient = location.state?.selectedClient ?? null;

  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(initialSelectedClient);
  const [searchedClient, setSearchedClient] = useState(initialSelectedClient);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedContractForInvoices, setSelectedContractForInvoices] = useState(null);

  // Load clients on component mount
  useEffect(() => {
    let active = true;
    const loadClients = async () => {
      setClientsLoading(true);
      try {
        const list = await getCacheClients();
        if (!active) return;
        setClients(list || []);

        if (initialSelectedClient && list) {
          const resolved = list.find(c => String(c.id) === String(initialSelectedClient.id));
          if (resolved && active) {
            setSelectedClient(resolved);
            setSearchedClient(resolved);
          }
        }
      } catch (err) {
        if (active) setClients([]);
      } finally {
        if (active) setClientsLoading(false);
      }
    };
    loadClients();
    return () => { active = false; };
  }, [initialSelectedClient]);

  useEffect(() => {
    if (initialSelectedClient && !hasSearched) {
      setHasSearched(true);
    }
  }, [initialSelectedClient, hasSearched])

  return (
    <BaseWrapper
      titleIcon={<ReceiptLongIcon />}
      title="Billing"
      subTitle="Manage Contracts"
      subTitleRoute={"/manage-contracts"}
    >
      {/* Client Selection Section and Create Contract button*/}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
            Select Client
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <Autocomplete
              options={clients}
              getOptionLabel={(client) => client?.name || ''}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              value={selectedClient}
              onChange={(e, value) => setSelectedClient(value)}
              loading={clientsLoading}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span>{option.name}</span>
                    <small style={{ opacity: .7 }}>ID: {option.id}</small>
                  </div>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Choose a client"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {clientsLoading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              sx={{ maxWidth: 400, flex: 1 }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setSearchedClient(selectedClient);
                setHasSearched(true);
              }}
              disabled={clientsLoading}
            >
              Search
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Contracts Table Section */}
      {searchedClient && hasSearched && (
        <Box sx={{ my: 3 }}>
          <ContractsTable
            source='manageContracts'
            selectedClient={searchedClient}
            hasSearched={hasSearched}
          />
        </Box>
      )}
      
      {/* Invoices Section */}
      {searchedClient && hasSearched && (
        <>
          <Box sx={{ my: 3 }}>
            <InvoicesTable
              source="manageContracts"
              selectedClient={searchedClient}
              hasSearched={hasSearched}
            />

          </Box>
        </>
      )}
      {/* Add Invoice Button */}
      
    </BaseWrapper>
  );
}

export default ManageContracts;